import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { LeadVisit, VisitListResponse, VisitSlot } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

export type VisitScope = "upcoming" | "history";

type VisitActionResponse = { visit: LeadVisit; leadId: string };

export function useVisits(scope: VisitScope, page: number) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["visits", clientId, scope, page],
    queryFn: () =>
      api.get<VisitListResponse>(`/crm/visits?scope=${scope}&page=${page}&pageSize=20`),
    enabled: !!clientId,
  });
}

export function useVisitSlots(excludeBookingId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["visit-slots", excludeBookingId ?? "none"],
    queryFn: () =>
      api.get<{ slots: VisitSlot[] }>(
        excludeBookingId
          ? `/crm/visits/slots?excludeBookingId=${excludeBookingId}`
          : "/crm/visits/slots",
      ),
    enabled,
    // Availability changes under our feet (agent bookings, other admins) —
    // always refetch when the picker opens.
    staleTime: 0,
  });
}

function useVisitAction(
  mutationFn: (vars: { bookingId: string; body: unknown }) => Promise<VisitActionResponse>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["visits"] });
      void queryClient.invalidateQueries({ queryKey: ["visit-slots"] });
      // Nested under ["lead", leadId] so LeadDetail's visit card + events refresh.
      void queryClient.invalidateQueries({ queryKey: ["lead", data.leadId, "visit"] });
      void queryClient.invalidateQueries({
        queryKey: ["lead", data.leadId, "visit-events"],
      });
      toast.success(successMessage);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useRescheduleVisit() {
  return useVisitAction(
    ({ bookingId, body }) =>
      api.post<VisitActionResponse>(`/crm/visits/${bookingId}/reschedule`, body),
    "Visita reprogramada.",
  );
}

export function useCancelVisit() {
  return useVisitAction(
    ({ bookingId, body }) =>
      api.post<VisitActionResponse>(`/crm/visits/${bookingId}/cancel`, body),
    "Visita cancelada.",
  );
}

export function useMarkAttendance() {
  return useVisitAction(
    ({ bookingId, body }) =>
      api.post<VisitActionResponse>(`/crm/visits/${bookingId}/attendance`, body),
    "Asistencia registrada.",
  );
}
